import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// В шестой версии default-экспорта у maplibre-gl нет вовсе: только именованные.
import { GPUInitializationError, Map as MapLibreMap, Popup, setWorkerUrl } from 'maplibre-gl';
import { AIRPORTS_CORE } from '@jetopolis/data';
import { TOWER_DARK_STYLE, applyTowerColors } from './style/tower-dark';

/**
 * Воркер берётся из статики, а не из бандлера.
 *
 * Он импортирует соседний ./maplibre-gl-shared.mjs относительным путём, и любая
 * обработка бандлером эту пару разрывает: разные хэши либо невыложенный сосед.
 * Ошибки при этом НЕТ ни одной - стиль просто навсегда остаётся незагруженным,
 * тайлы не запрашиваются, карта чёрная. Оба файла кладёт scripts/copy-map-worker.ts.
 */
setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');

const AIRPORTS_SOURCE = 'jt-airports';
const AIRPORTS_LAYER = 'jt-airports-circles';

interface AirportFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: {
    iata: string;
    name: string;
    city: string;
    country: string;
    marketSize: number;
  };
}

interface AirportCollection {
  type: 'FeatureCollection';
  features: AirportFeature[];
}

function airportsGeoJson(): AirportCollection {
  return {
    type: 'FeatureCollection',
    features: AIRPORTS_CORE.map((a) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
      properties: {
        iata: a.iata,
        name: a.name,
        city: a.city,
        country: a.country,
        marketSize: a.marketSize,
      },
    })),
  };
}

export function MapView() {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const node = container.current;
    if (!node) return;

    let map: MapLibreMap;
    try {
      map = new MapLibreMap({
        container: node,
        style: TOWER_DARK_STYLE,
        center: [10, 30],
        zoom: 1.6,
        attributionControl: { compact: true },
        // Вращение карты игре не нужно: она читается как схема, а не как вид.
        dragRotate: false,
        pitchWithRotate: false,
      });
    } catch (e) {
      // С 6.7 конструктор КИДАЕТ исключение при отсутствии WebGL2, а не стреляет
      // событием error: старый паттерн map.on('error') этот случай не поймает.
      if (e instanceof GPUInitializationError) {
        // Через микрозадачу: синхронный setState прямо в эффекте вызывает
        // каскад перерисовок, а показать плашку можно и следующим кадром.
        queueMicrotask(() => {
          setFailed(true);
        });
        return;
      }
      throw e;
    }

    map.touchZoomRotate.disableRotation();

    map.on('error', (e) => {
      console.error('[map]', e.error?.message ?? e);
    });

    const popup = new Popup({ closeButton: true, closeOnClick: true, maxWidth: '260px' });

    map.on('load', () => {
      applyTowerColors(map);

      map.addSource(AIRPORTS_SOURCE, { type: 'geojson', data: airportsGeoJson() });
      map.addLayer({
        id: AIRPORTS_LAYER,
        type: 'circle',
        source: AIRPORTS_SOURCE,
        // На мелком зуме мелкие аэропорты только зашумляют карту (05 §8).
        filter: ['any', ['>=', ['zoom'], 3], ['>=', ['get', 'marketSize'], 3]],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'marketSize'], 2, 2.5, 10, 9],
          'circle-color': ['step', ['get', 'marketSize'], '#39506b', 3, '#5b7ea6', 7, '#f2a33c'],
          'circle-opacity': 0.9,
          'circle-stroke-width': 0.5,
          'circle-stroke-color': '#0b0f14',
        },
      });

      map.on('click', AIRPORTS_LAYER, (e) => {
        const f = e.features?.[0];
        if (!f) return;
        // В шестой версии вложенные свойства приходят объектами, а не строками:
        // JSON.parse здесь был бы ошибкой.
        const p = f.properties as {
          iata: string;
          name: string;
          city: string;
          country: string;
          marketSize: number;
        };
        popup
          .setLngLat(e.lngLat)
          .setHTML(
            `<div class="jt-popup">` +
              `<div class="jt-popup-iata">${p.iata}</div>` +
              `<div class="jt-popup-name">${p.name}</div>` +
              `<div class="jt-popup-sub">${p.city}, ${p.country}</div>` +
              `<div class="jt-popup-sub">${t('hq.airport.marketSize')}: ${p.marketSize}</div>` +
              `</div>`,
          )
          .addTo(map);
      });
      map.on('mouseenter', AIRPORTS_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', AIRPORTS_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });
    });

    /*
     * Наблюдаем за КОНТЕЙНЕРОМ, а не за окном. MapLibre читает размер один раз
     * при создании: если в этот момент flex-родитель ещё не получил высоту,
     * канвас остаётся нулевым, и карта не запрашивает НИ ОДНОГО тайла - при
     * этом ошибок нет, просто чёрный прямоугольник. Событие resize у окна
     * такой случай не ловит: окно не менялось.
     */
    const observer = new ResizeObserver(() => {
      map.resize();
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      // Без remove StrictMode в разработке смонтирует ДВЕ карты и два
      // контекста WebGL2: эффект вызывается дважды намеренно.
      map.remove();
    };
  }, [t]);

  if (failed) {
    return (
      <div className="flex h-full items-center justify-center bg-ink p-6 text-center text-[13px] text-mut">
        {t('errors.map.webgl')}
      </div>
    );
  }

  return <div ref={container} className="h-full w-full bg-ink" aria-label="map" />;
}
